import {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import {
  ClientCategoryDto,
  ClientDto,
} from "../../../modules/Admin/ClientManagement/types";
// WebP: PNG halleri 1.9 MB / 1.6 MB idi ve hero'nun LCP öğesi bu.
import buildingNight from "../../assets/securitas-building-night.webp";
import buildingDay from "../../assets/securitas-building.webp";
import { normalizeColor } from "../../config/cardColors";
import {
  activeCategories,
  findCategory,
  getCategoryName,
  iconKeyOf,
} from "../../config/categories";
import { REDESIGN_NS } from "../../i18n";
import {
  buildPortalUrl,
  getPortalDescription,
  getPortalName,
  openPortal,
} from "../../lib/openPortal";
import { fetchClientCategories } from "../../lib/clientCategories";
import { fetchUserClients } from "../../lib/userClients";
import {
  fetchUserPreferences,
  saveUserPreferences,
} from "../../lib/userPreferences";
import {
  IconGridView,
  IconListView,
  IconSearch,
  IconSliders,
} from "../../ui/icons";
import Select from "../../ui/Select";
import ColorPicker from "./ColorPicker";
import PortalCard from "./PortalCard";
import "./portal-selection.css";

type SortMode = "default" | "az" | "za";
type ViewMode = "grid" | "list";

const ALL = 0;

type ColorMap = Record<string, string | null>;
type OrderMap = Record<string, number | null>;

/**
 * Kullanıcının kendi sırası "Varsayılan" görünümün yerine geçer; ayrı bir sıralama
 * seçeneği açılmaz, çünkü müşterinin istediği bu sıranın varsayılan olarak kaydedilmesi.
 * Sırası olmayan portaller API sırasını koruyarak arkaya eklenir: yeni bir portal
 * tanımlandığında kullanıcının düzeni bozulmasın diye.
 */
function applyUserOrder(list: ClientDto[], order: OrderMap): ClientDto[] {
  const ranked = list.filter((client) => typeof order[client.id] === "number");
  if (ranked.length === 0) return list;

  const rest = list.filter((client) => typeof order[client.id] !== "number");
  ranked.sort(
    (a, b) => (order[a.id] as number) - (order[b.id] as number)
  );
  return ranked.concat(rest);
}

/* `inject`/`observer` yok: bileşen hiçbir MobX observable'ı okumuyor. */
const PortalSelectionView: FC = () => {
  const { t, i18n } = useTranslation(REDESIGN_NS);

  const [clients, setClients] = useState<ClientDto[]>([]);
  const [categories, setCategories] = useState<ClientCategoryDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<number>(ALL);
  const [sort, setSort] = useState<SortMode>("default");
  const [view, setView] = useState<ViewMode>("grid");
  const nightImgRef = useRef<HTMLImageElement>(null);

  const [colors, setColors] = useState<ColorMap>({});
  const [order, setOrder] = useState<OrderMap>({});

  // Kişiselleştirme modunun kendi taslağı var; kaydedilmeden çıkılırsa hiçbir şey değişmez.
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [draftOrder, setDraftOrder] = useState<ClientDto[]>([]);
  const [draftColors, setDraftColors] = useState<ColorMap>({});
  const [isSaving, setIsSaving] = useState(false);
  const dragIndex = useRef<number | null>(null);

  /*
   * Gece görselini peşinen çöz: `opacity: 0` olduğu için tarayıcı çözmeyi ilk görünür
   * olduğu ana, yani tema değişiminin ilk karesine erteleyebiliyor. Ölçüldü: 26.5 ms,
   * tek kare bütçesinden fazla.
   */
  useEffect(() => {
    nightImgRef.current?.decode().catch(() => undefined);
  }, []);

  // Kabuktaki mega menü de aynı listeyi istiyor; `fetchUserClients` uçuştaki isteği paylaştırır.
  useEffect(() => {
    Promise.all([
      fetchUserClients(),
      fetchClientCategories(),
      fetchUserPreferences(),
    ]).then(([result, categoryList, preferences]) => {
      setClients(result);
      setCategories(categoryList);

      const nextColors: ColorMap = {};
      const nextOrder: OrderMap = {};
      preferences.forEach((preference) => {
        nextColors[preference.clientId] = normalizeColor(preference.colorKey);
        nextOrder[preference.clientId] = preference.orderIndex;
      });
      setColors(nextColors);
      setOrder(nextOrder);

      setIsLoading(false);
    });
  }, []);

  const language = i18n.language;

  /*
   * Yalnızca kullanıcının en az bir portalı bulunan kategoriler gösterilir. Ölçüt
   * ARAMADAN bağımsızdır (`clients`, `visible` değil): arama yazarken çiplerin belirip
   * kaybolması filtre çubuğunu zıplatırdı. Mega menü de aynı kuralı uyguluyor.
   */
  const availableCategories = useMemo(() => {
    const present = new Set(clients.map((client) => client.clientCategoryId));
    return activeCategories(categories).filter((def) => present.has(def.id));
  }, [clients, categories]);

  const visible = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("tr");

    const filtered = clients.filter((client) => {
      const def = findCategory(categories, client.clientCategoryId);
      if (category !== ALL && client.clientCategoryId !== category) return false;
      if (!needle) return true;

      const haystack = [
        getPortalName(client, language),
        getPortalDescription(client, language),
        getCategoryName(def, language),
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr");

      return haystack.indexOf(needle) >= 0;
    });

    // "Varsayılan" API sırasıdır; kullanıcı kendi sırasını kaydettiyse onun yerine geçer.
    if (sort === "default") return applyUserOrder(filtered, order);

    const sorted = [...filtered].sort((a, b) =>
      getPortalName(a, language).localeCompare(getPortalName(b, language), "tr")
    );
    return sort === "za" ? sorted.reverse() : sorted;
  }, [clients, categories, query, category, sort, language, order]);

  /*
   * Kişiselleştirmeye girerken filtreler sıfırlanır: bir alt kümeyi sürükleyip
   * kaydetmek listenin geri kalanı için anlamsız bir sıra üretirdi.
   */
  const startCustomizing = useCallback(() => {
    setQuery("");
    setCategory(ALL);
    setSort("default");
    setDraftOrder(applyUserOrder(clients, order));
    setDraftColors(colors);
    setIsCustomizing(true);
  }, [clients, order, colors]);

  const cancelCustomizing = useCallback(() => {
    setIsCustomizing(false);
    setDraftOrder([]);
  }, []);

  /*
   * Sıfırlama da taslak üzerinde çalışır: sıra API'nin verdiği sıraya, renkler
   * boşa döner ve kalıcı olması için Kaydet'e basılması gerekir.
   */
  const resetCustomizing = useCallback(() => {
    setDraftOrder(clients);
    setDraftColors({});
  }, [clients]);

  const commitCustomizing = useCallback(() => {
    setIsSaving(true);

    const items = draftOrder.map((client, index) => ({
      clientId: client.id,
      colorKey: draftColors[client.id] || null,
      orderIndex: index,
    }));

    saveUserPreferences({ items }).then((isSaved) => {
      setIsSaving(false);
      // Başarı ve hata toast'ını axios interceptor basıyor.
      if (!isSaved) return;

      const nextOrder: OrderMap = {};
      items.forEach((item) => {
        nextOrder[item.clientId] = item.orderIndex;
      });
      setOrder(nextOrder);
      setColors(draftColors);
      setIsCustomizing(false);
      setDraftOrder([]);
    });
  }, [draftOrder, draftColors]);

  /* Sürüklenen kart üzerinden geçilen kartın yerine ANINDA taşınır; bırakma anını
     beklemek kullanıcıya nereye düşeceğini göstermiyordu. */
  const onDragOverSlot = useCallback((index: number) => {
    const from = dragIndex.current;
    if (from === null || from === index) return;

    setDraftOrder((current) => {
      const next = current.slice();
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
    dragIndex.current = index;
  }, []);

  const hero = (
    <section className="sso-rd__hero">
      {/* Sarmalayıcı yalnızca maske taşır; gerekçesi CSS'te. */}
      <span className="sso-rd__hero-decor" aria-hidden="true">
        <span className="sso-rd__hero-circle sso-rd__hero-circle--1" />
        <span className="sso-rd__hero-circle sso-rd__hero-circle--2" />
      </span>

      <div className="sso-rd__hero-grid">
        <div className="sso-rd__hero-text">
          {/* Metin kaynakta BÜYÜK yazılır; text-transform kullanılmaz (README Gotcha 1). */}
          <div className="sso-rd__hero-eyebrow">{t("Company Name")}</div>
          <h1 className="sso-rd__hero-title">
            {t("Please select the portal you want to login.")}
          </h1>

          <div className="sso-rd__hero-search">
            <IconSearch size={19} />
            <input
              type="text"
              value={query}
              placeholder={t("Search for a portal, service or keyword...")}
              onChange={(event) => setQuery(event.target.value)}
              aria-label={t("Search for a portal, service or keyword...")}
              disabled={isCustomizing}
            />
          </div>
        </div>

        <div className="sso-rd__hero-media">
          <div className="sso-rd__hero-image">
            <div className="sso-rd__hero-img-stack">
              <img className="sso-rd__hero-img" src={buildingDay} alt="" />
              <img
                ref={nightImgRef}
                className="sso-rd__hero-img sso-rd__hero-img--night"
                src={buildingNight}
                alt=""
              />
            </div>
          </div>
          <div className="sso-rd__hero-fade" />
        </div>
      </div>
    </section>
  );

  const filterBar = (
    <div className="sso-rd__filters">
      <div className="sso-rd__cats">
        <button
          type="button"
          className={"sso-rd__cat" + (category === ALL ? " is-active" : "")}
          onClick={() => setCategory(ALL)}
        >
          {t("All")}
        </button>
        {availableCategories.map((def) => (
          <button
            key={def.id}
            type="button"
            className={
              "sso-rd__cat" + (category === def.id ? " is-active" : "")
            }
            onClick={() => setCategory(def.id)}
          >
            {getCategoryName(def, language)}
          </button>
        ))}
      </div>

      <div className="sso-rd__filters-right">
        <Select
          value={sort}
          onChange={(next) => setSort(next as SortMode)}
          options={[
            { value: "default", label: t("Default") },
            { value: "az", label: t("A - Z") },
            { value: "za", label: t("Z - A") },
          ]}
          ariaLabel={t("Sort")}
        />

        <div className="sso-rd__view-switch">
          <button
            type="button"
            className={
              "sso-rd__view-btn" + (view === "grid" ? " is-active" : "")
            }
            onClick={() => setView("grid")}
            title={t("Grid View")}
            aria-label={t("Grid View")}
            aria-pressed={view === "grid"}
          >
            <IconGridView size={17} />
          </button>
          <button
            type="button"
            className={
              "sso-rd__view-btn" + (view === "list" ? " is-active" : "")
            }
            onClick={() => setView("list")}
            title={t("List View")}
            aria-label={t("List View")}
            aria-pressed={view === "list"}
          >
            <IconListView size={17} />
          </button>
        </div>

        <button
          type="button"
          className="sso-rd__personalize"
          onClick={startCustomizing}
          title={t("Personalize")}
          aria-label={t("Personalize")}
        >
          <IconSliders size={17} />
          <span>{t("Personalize")}</span>
        </button>
      </div>
    </div>
  );

  const customizeBar = (
    <div className="sso-rd__customize">
      <span className="sso-rd__customize-hint">
        {t("Drag the cards to set your own order and pick a color for each.")}
      </span>

      <div className="sso-rd__customize-actions">
        <button
          type="button"
          className="sso-rd__btn sso-rd__btn--ghost"
          onClick={resetCustomizing}
          disabled={isSaving}
        >
          {t("Reset to default")}
        </button>
        <button
          type="button"
          className="sso-rd__btn sso-rd__btn--secondary"
          onClick={cancelCustomizing}
          disabled={isSaving}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className={
            "sso-rd__btn sso-rd__btn--primary" + (isSaving ? " is-busy" : "")
          }
          onClick={commitCustomizing}
          disabled={isSaving}
          aria-busy={isSaving}
        >
          {isSaving && <span className="sso-rd__spinner" aria-hidden="true" />}
          {t("Save")}
        </button>
      </div>
    </div>
  );

  const shown = isCustomizing ? draftOrder : visible;

  const portalGrid = (
    <div
      className={
        "sso-rd__portal-grid sso-rd__portal-grid--" +
        view +
        (isCustomizing ? " is-customizing" : "")
      }
    >
      {shown.map((client, index) => {
        const def = findCategory(categories, client.clientCategoryId);
        const color = isCustomizing
          ? draftColors[client.id] || null
          : colors[client.id] || null;

        return (
          <div
            key={client.id}
            className="sso-rd__portal-slot"
            /* Renk hem karta hem seçicinin düğmesine lazım, ikisinin ortak atası burası. */
            data-color={color || undefined}
            style={color ? ({ "--sso-card-tint": color } as CSSProperties) : undefined}
            /* Bırakma hedefi slot'un tamamı; sürükleme kaynağı ise yalnızca içteki sarmalayıcı. */
            onDragOver={(event) => {
              if (!isCustomizing) return;
              event.preventDefault();
              onDragOverSlot(index);
            }}
          >
            {/*
             * Sürüklenen düğüm renk seçiciyi KAPSAMAMALI: seçici slot'un içindeyken
             * karedeki bas-sürükle hareketi tarayıcının kart sürüklemesini başlatıyor
             * ve renk seçilemiyordu. Seçici bu sarmalayıcının kardeşi olarak duruyor.
             */}
            <div
              className="sso-rd__portal-drag"
              draggable={isCustomizing}
              onDragStart={(event) => {
                dragIndex.current = index;
                event.dataTransfer.effectAllowed = "move";
                // Firefox sürüklemeyi yalnızca veri yazılmışsa başlatıyor.
                event.dataTransfer.setData("text/plain", client.id);
              }}
              onDragEnd={() => {
                dragIndex.current = null;
              }}
            >
              <PortalCard
                name={getPortalName(client, language)}
                description={getPortalDescription(client, language)}
                icon={iconKeyOf(def)}
                categoryLabel={getCategoryName(def, language)}
                isCustomizing={isCustomizing}
                buildHref={() => buildPortalUrl(client)}
                onOpen={() => openPortal(client)}
              />
            </div>

            {isCustomizing && (
              <ColorPicker
                value={color}
                onChange={(next) =>
                  setDraftColors((current) => ({
                    ...current,
                    [client.id]: next,
                  }))
                }
              />
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      {hero}

      <div className="sso-rd__container">
        {isCustomizing ? customizeBar : filterBar}

        {isLoading ? (
          <Loading />
        ) : shown.length === 0 ? (
          <div className="sso-rd__empty">
            {t("No portals matched your search.")}
          </div>
        ) : (
          portalGrid
        )}
      </div>
    </>
  );
};

export default PortalSelectionView;
